import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import TransactionFilters from "@/shared/components/transactions/TransactionFilters";
import TransactionTable from "@/shared/components/transactions/TransactionTable";
import TransactionDetail from "@/shared/components/transactions/TransactionDetail";
import * as qrCodeService from "@/shared/api/services/qrCodeService";
import * as adminService from "@/shared/api/services/adminService";
import * as providerService from "@/shared/api/services/providerService";
import * as combinedTransactionService from "@/shared/api/services/combinedTransactionService";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { ChevronLeft, ChevronRight, ArrowDownToLine, ArrowUpFromLine, Activity, Layers, Sparkles } from "lucide-react";

/* ══════════════════════════════════════════════
   GLOBAL CSS — injected once into <head>
   No animations except the live-dot pulse &
   spinner. All hover = pure CSS transition.
══════════════════════════════════════════════ */
const TX_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

/* ── base ── */
.tx { display:flex; flex-direction:column; gap:1.25rem; background:#060C18; min-height:100%; padding:1.25rem; font-family:'Outfit',system-ui,sans-serif; }
.tx * { font-family:'Outfit',system-ui,sans-serif !important; }

/* nuclear dark — override any leftover Tailwind whites */
.tx .bg-white,.tx .bg-slate-50,.tx .bg-slate-100,.tx .bg-gray-50,.tx [class*="bg-white"],[class*="bg-slate-5"] { background:#0E1C35 !important; }
.tx .text-slate-900,.tx .text-slate-800,.tx .text-gray-900 { color:#EFF6FF !important; }
.tx .text-slate-600,.tx .text-slate-500,.tx .text-gray-500 { color:#93B4D4 !important; }
.tx .text-slate-400 { color:#4D6E90 !important; }
.tx .border-slate-200,.tx .border-slate-100 { border-color:rgba(255,255,255,0.07) !important; }
.tx .shadow-sm,.tx .shadow { box-shadow:none !important; }

/* ── header ── */
.tx-hdr { display:flex; flex-wrap:wrap; align-items:flex-start; justify-content:space-between; gap:1rem; }
.tx-hdr-l { display:flex; flex-direction:column; gap:0.3rem; }
.tx-live { display:flex; align-items:center; gap:0.45rem; }
.tx-dot-wrap { position:relative; width:0.55rem; height:0.55rem; }
.tx-dot { position:absolute; inset:0; border-radius:50%; background:#10B981; }
.tx-dot-ring { position:absolute; inset:0; border-radius:50%; background:#10B981; animation:tx-ping 2s ease-in-out infinite; }
@keyframes tx-ping { 0%,100%{transform:scale(1);opacity:.5} 50%{transform:scale(2);opacity:0} }
.tx-live-lbl { font-size:0.58rem; font-weight:800; letter-spacing:0.18em; text-transform:uppercase; color:#4D6E90; }
.tx-title { font-size:1.5rem; font-weight:800; color:#EFF6FF; letter-spacing:-0.02em; margin:0; }
.tx-count { font-size:0.78rem; font-weight:500; color:#4D6E90; margin:0; }
.tx-count b { color:#22D3EE; }

/* stat chips */
.tx-chips { display:flex; align-items:center; gap:0.5rem; flex-wrap:wrap; }
.tx-chip { display:flex; align-items:center; gap:0.45rem; padding:0.35rem 0.875rem; border-radius:0.625rem; border:1px solid transparent; cursor:default; transition:background 120ms ease, border-color 120ms ease; }
.tx-chip-ico { width:0.8rem; height:0.8rem; flex-shrink:0; }
.tx-chip-lbl { font-size:0.6rem; font-weight:800; letter-spacing:0.16em; text-transform:uppercase; }
.tx-chip-val { font-size:0.8125rem; font-weight:700; }

.tx-chip-c { background:rgba(34,211,238,0.08); border-color:rgba(34,211,238,0.18); }
.tx-chip-c .tx-chip-lbl { color:rgba(34,211,238,0.6); }
.tx-chip-c .tx-chip-val { color:#22D3EE; }
.tx-chip-c:hover { background:rgba(34,211,238,0.13); border-color:rgba(34,211,238,0.28); }

.tx-chip-e { background:rgba(16,185,129,0.08); border-color:rgba(16,185,129,0.18); }
.tx-chip-e .tx-chip-lbl { color:rgba(16,185,129,0.6); }
.tx-chip-e .tx-chip-val { color:#34D399; }
.tx-chip-e:hover { background:rgba(16,185,129,0.13); border-color:rgba(16,185,129,0.28); }

.tx-chip-p { background:rgba(167,139,250,0.08); border-color:rgba(167,139,250,0.18); }
.tx-chip-p .tx-chip-lbl { color:rgba(167,139,250,0.6); }
.tx-chip-p .tx-chip-val { color:#A78BFA; }
.tx-chip-p:hover { background:rgba(167,139,250,0.13); border-color:rgba(167,139,250,0.28); }

/* ── panels ── */
.tx-panel { background:#0E1C35; border:1px solid rgba(255,255,255,0.07); border-radius:0.875rem; overflow:hidden; transition:border-color 150ms ease; }
.tx-panel:hover { border-color:rgba(34,211,238,0.14); }

/* panel header bar */
.tx-panel-bar { display:flex; align-items:center; justify-content:space-between; padding:0.75rem 1rem; border-bottom:1px solid rgba(255,255,255,0.06); }
.tx-panel-bar-l { display:flex; align-items:center; gap:0.55rem; }
.tx-panel-icon { width:1.6rem; height:1.6rem; border-radius:0.4rem; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.tx-panel-icon-c { background:rgba(34,211,238,0.08); border:1px solid rgba(34,211,238,0.16); }
.tx-panel-icon-b { background:rgba(59,130,246,0.08); border:1px solid rgba(59,130,246,0.16); }
.tx-panel-icon svg { width:0.8rem; height:0.8rem; }
.tx-panel-icon-c svg { color:#22D3EE; }
.tx-panel-icon-b svg { color:#60A5FA; }
.tx-panel-title { font-size:0.65rem; font-weight:800; letter-spacing:0.1em; text-transform:uppercase; color:#4D6E90; }
.tx-line { flex:1; height:1px; background:linear-gradient(90deg,rgba(34,211,238,0.10),transparent); margin-left:0.25rem; }

/* export btn in panel bar */
.tx-export-btn { display:inline-flex; align-items:center; gap:0.3rem; padding:0 0.75rem; height:1.875rem; border-radius:0.5rem; font-size:0.72rem; font-weight:700; background:rgba(34,211,238,0.08); border:1px solid rgba(34,211,238,0.18); color:#22D3EE; cursor:pointer; transition:background 120ms ease, border-color 120ms ease; }
.tx-export-btn svg { width:0.72rem; height:0.72rem; }
.tx-export-btn:hover { background:rgba(34,211,238,0.14); border-color:rgba(34,211,238,0.32); }

/* syncing badge */
.tx-sync { display:inline-flex; align-items:center; gap:0.3rem; padding:0.2rem 0.55rem; border-radius:0.4rem; background:rgba(34,211,238,0.07); border:1px solid rgba(34,211,238,0.14); }
.tx-sync-ico { width:0.65rem; height:0.65rem; color:#22D3EE; animation:tx-spin 1s linear infinite; }
@keyframes tx-spin { to{transform:rotate(360deg)} }
.tx-sync-lbl { font-size:0.6rem; font-weight:800; color:#22D3EE; }

/* ── filter inner padding ── */
.tx-filters-pad { padding:0.875rem 1rem; }

/* ── TABLE overrides — nuclear dark ── */
.tx-table-wrap,.tx-table-wrap>*,.tx-table-wrap [class*="rounded"],.tx-table-wrap .bg-white,.tx-table-wrap .bg-slate-50 { background:transparent !important; }
.tx-table-wrap table { border-collapse:collapse !important; width:100% !important; }
.tx-table-wrap thead,.tx-table-wrap thead tr,.tx-table-wrap thead th { background:rgba(255,255,255,0.02) !important; }
.tx-table-wrap th { color:#3E5E7E !important; font-size:10px !important; font-weight:800 !important; letter-spacing:0.14em !important; text-transform:uppercase !important; border-bottom:1px solid rgba(255,255,255,0.06) !important; padding:11px 16px !important; white-space:nowrap !important; }
.tx-table-wrap tbody tr { background:transparent !important; border-bottom:1px solid rgba(255,255,255,0.04) !important; transition:background 100ms ease !important; cursor:pointer !important; }
.tx-table-wrap tbody tr:last-child { border-bottom:none !important; }
.tx-table-wrap tbody tr:hover { background:rgba(34,211,238,0.045) !important; }
.tx-table-wrap tbody tr:hover td:first-child { border-left:3px solid #22D3EE !important; }
.tx-table-wrap td { background:transparent !important; padding:12px 16px !important; font-size:13px !important; font-weight:500 !important; vertical-align:middle !important; transition:color 100ms ease !important; }
.tx-table-wrap td,.tx-table-wrap td * { color:#93B4D4 !important; }
.tx-table-wrap tbody tr:hover td,.tx-table-wrap tbody tr:hover td * { color:#EFF6FF !important; }
.tx-table-wrap .font-medium,.tx-table-wrap .font-semibold,.tx-table-wrap .font-bold { color:#B8D4EE !important; }

/* badge overrides */
.tx-table-wrap [class*="bg-emerald"],.tx-table-wrap [class*="bg-green"] { background:rgba(16,185,129,0.12) !important; color:#34D399 !important; border:1px solid rgba(16,185,129,0.25) !important; border-radius:6px !important; font-size:11px !important; font-weight:700 !important; }
.tx-table-wrap [class*="bg-amber"],.tx-table-wrap [class*="bg-yellow"]   { background:rgba(251,191,36,0.12) !important; color:#FBBF24 !important; border:1px solid rgba(251,191,36,0.25) !important; border-radius:6px !important; font-size:11px !important; font-weight:700 !important; }
.tx-table-wrap [class*="bg-red"]  { background:rgba(251,113,133,0.12) !important; color:#FB7185 !important; border:1px solid rgba(251,113,133,0.25) !important; border-radius:6px !important; font-size:11px !important; font-weight:700 !important; }
.tx-table-wrap [class*="bg-blue"] { background:rgba(59,130,246,0.12) !important; color:#60A5FA !important; border:1px solid rgba(59,130,246,0.25) !important; border-radius:6px !important; font-size:11px !important; font-weight:700 !important; }

/* table action buttons */
.tx-table-wrap button:not([disabled]) { background:rgba(34,211,238,0.07) !important; border:1px solid rgba(34,211,238,0.16) !important; color:#22D3EE !important; border-radius:6px !important; font-size:12px !important; font-weight:600 !important; transition:background 100ms ease, border-color 100ms ease !important; }
.tx-table-wrap button:not([disabled]):hover { background:rgba(34,211,238,0.13) !important; border-color:rgba(34,211,238,0.32) !important; color:#67E8F9 !important; }
.tx-table-wrap button svg { color:inherit !important; }

/* scrollbar */
.tx-table-wrap::-webkit-scrollbar,.tx-panel::-webkit-scrollbar { width:4px; height:4px; }
.tx-table-wrap::-webkit-scrollbar-track,.tx-panel::-webkit-scrollbar-track { background:transparent; }
.tx-table-wrap::-webkit-scrollbar-thumb,.tx-panel::-webkit-scrollbar-thumb { background:rgba(34,211,238,0.18); border-radius:99px; }

/* ── FILTER INNER (tx-filters class on wrapper) ── */
.tx-filters input,[class*="tx-filters"] [type="text"] { background:#0A1628 !important; border:1px solid rgba(255,255,255,0.09) !important; color:#EFF6FF !important; border-radius:0.55rem !important; transition:border-color 120ms ease !important; }
.tx-filters input::placeholder { color:#4D6E90 !important; }
.tx-filters input:focus { border-color:#22D3EE !important; box-shadow:0 0 0 2px rgba(34,211,238,0.14) !important; }
.tx-filters button[role="combobox"],.tx-filters [data-radix-select-trigger] { background:#0A1628 !important; border:1px solid rgba(255,255,255,0.09) !important; color:#EFF6FF !important; border-radius:0.55rem !important; transition:border-color 120ms ease !important; }
.tx-filters button[role="combobox"]:hover { border-color:rgba(34,211,238,0.25) !important; background:#122040 !important; }
.tx-filters button:not([role="combobox"]) { background:#0A1628 !important; border:1px solid rgba(255,255,255,0.09) !important; color:#93B4D4 !important; border-radius:0.55rem !important; transition:background 120ms ease, border-color 120ms ease, color 120ms ease !important; }
.tx-filters button:not([role="combobox"]):hover { background:#122040 !important; border-color:rgba(34,211,238,0.25) !important; color:#22D3EE !important; }

/* ── PAGINATION ── */
.tx-pag { display:flex; align-items:center; justify-content:space-between; background:#0E1C35; border:1px solid rgba(255,255,255,0.07); border-radius:0.75rem; padding:0.55rem 0.875rem; }
.tx-pag-info { font-size:0.775rem; color:#4D6E90; }
.tx-pag-info b { color:#93B4D4; }
.tx-pag-info em { color:#22D3EE; font-style:normal; font-weight:700; }
.tx-pag-ctrl { display:flex; align-items:center; gap:0.35rem; }
.tx-pag-btn { width:1.875rem; height:1.875rem; border-radius:0.45rem; display:flex; align-items:center; justify-content:center; background:#122040; border:1px solid rgba(255,255,255,0.09); color:#93B4D4; cursor:pointer; transition:background 100ms ease, border-color 100ms ease, color 100ms ease; }
.tx-pag-btn svg { width:0.8rem; height:0.8rem; }
.tx-pag-btn:hover:not(:disabled) { background:rgba(34,211,238,0.10); border-color:rgba(34,211,238,0.35); color:#22D3EE; }
.tx-pag-btn:disabled { opacity:0.28; cursor:not-allowed; }
.tx-pag-pill { width:1.875rem; height:1.875rem; border-radius:0.4rem; display:flex; align-items:center; justify-content:center; font-size:0.72rem; font-weight:700; border:1px solid rgba(255,255,255,0.09); background:#122040; color:#93B4D4; cursor:pointer; transition:background 100ms ease, border-color 100ms ease, color 100ms ease; }
.tx-pag-pill:hover { background:rgba(34,211,238,0.08); border-color:rgba(34,211,238,0.25); color:#22D3EE; }
.tx-pag-pill.active { background:#22D3EE; border-color:#22D3EE; color:#060C18; cursor:default; }
`;

const PAGE_SIZE = 20;
const RESET_F = { search: "", status: "all", dateFrom: null, dateTo: null };

export default function Transactions() {
  const { t } = useLanguage();
  const [filters, setFilters] = useState(RESET_F);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);

  /* inject CSS once */
  useEffect(() => {
    const id = "tx-global-css";
    if (document.getElementById(id)) return;
    const el = Object.assign(document.createElement("style"), { id, textContent: TX_CSS });
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, []);

  /* queries */
  const { data: merchants = [] } = useQuery({ queryKey: ["merchants"], queryFn: () => adminService.getAllMerchants() });
  const { data: resellers = [] } = useQuery({ queryKey: ["resellers"], queryFn: () => adminService.getAllResellers() });

  const filtersMapper = React.useMemo(() => {
    const dFrom = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const dTo = filters.dateTo ? new Date(filters.dateTo) : null;
    return {
      status: filters.status !== "all" ? filters.status : undefined,
      startDate: dFrom && !isNaN(dFrom.getTime()) ? dFrom.toISOString() : undefined,
      endDate: dTo && !isNaN(dTo.getTime()) ? dTo.toISOString() : undefined,
    };
  }, [filters]);

  const merchantIds = React.useMemo(() => {
    if (filters.merchant !== "all") return [filters.merchant];
    return [];
  }, [filters.merchant]);

  const { data: combinedQRCodeAndWitData, isLoading: isLoadingQRAndWithdrawals, refetch: refetchWithdrawalsAndQr } = useQuery({
    queryKey: ["merchant-withdrawals", page, PAGE_SIZE, filters.search, filtersMapper, merchantIds],
    queryFn: () => combinedTransactionService.getCombinedQRCodeAndWitData(
      page + 1,
      PAGE_SIZE,
      filters.search,
      filtersMapper,
      merchantIds
    ),
  });

  const isLoading = isLoadingQRAndWithdrawals;
  const combinedDataWithdrawalsAndQRCodes = combinedQRCodeAndWitData?.data || [];

  const combinedData = React.useMemo(() => {
    return combinedDataWithdrawalsAndQRCodes.map((t) => ({
      ...t,
      transaction_id: t.transaction_id || t.payment_id || t.id,
      merchant_ref: t.merchant_ref || t.reference,
      type: t.type?.toUpperCase() || "UNKNOWN",
      status: (t.status || "").toLowerCase(),
      amount: t.amount,
      net_amount: t.net_amount,
      total_fee: t.total_fee,
      provider_display: t.bank_code || t.provider_code || "-",
      currency: t.currency,
      client_bank_name: t.client_bank_name || t.bank_name || t.bank_code,
      bank_name: t.bank_name,
      bank_code: t.bank_code,
      merchant: t.merchant || (t.merchant_id ? merchants.find(m => m.id === t.merchant_id) : null) || {
        name: t.merchant_name,
        id: t.merchant_id,
        code: t.merchant_code,
      },
      reseller: t.reseller || (t.merchant?.createdByResellerId ? resellers.find(r => r.id === t.merchant.createdByResellerId) : null) || {
        name: t.reseller_name,
        id: t.reseller_id,
        code: t.reseller_code,
      },
      created_date: t.created_date || t.createdAt,
      updated_date: t.updated_date || t.updatedAt,
      processed_at: t.processed_at || t.updatedAt,
      completed_at: t.processed_at || t.updatedAt,
    })).sort((a, b) => new Date(b.created_date || b.createdAt) - new Date(a.created_date || a.createdAt));
  }, [combinedDataWithdrawalsAndQRCodes, merchants, resellers]);

  const paginated = combinedData;
  const totalPages = combinedQRCodeAndWitData?.totalPages || 0;
  const totalTransactions = combinedQRCodeAndWitData?.total || 0;
  const depCount = combinedData.filter((t) => t.type === "DEPOSIT" || t.type === "Deposit" || t.type === "deposit").length;
  const wdCount = combinedData.filter((t) => t.type === "WITHDRAWAL" || t.type === "Withdrawal" || t.type === "withdrawal").length;

  const providerOptions = useMemo(() => {
    const providerNames = combinedData.map((tx) => {
      if (tx.type === "DEPOSIT" || tx.type === "Deposit" || tx.type === "deposit") {
        return tx.client_bank_name;
      } else {
        return tx.bank_name || tx.bank_code;
      }
    }).filter(Boolean);
    return Array.from(new Set(providerNames));
  }, [combinedData]);

  const handleFilter = useCallback((f) => { setFilters(f); setPage(0); }, []);
  const handleReset = useCallback(() => { setFilters(RESET_F); setPage(0); }, []);
  const handleExport = useCallback(() => {
    const hdr = [t("type"), t("paymentId"), t("reference"), t("merchant"), t("reseller"), t("provider"), t("amount"), t("status"), t("date")];
    const rows = combinedData.map((tx) => [
      tx.type === "WITHDRAWAL" || tx.type === "withdrawal" || tx.type === "Withdrawal" ? t("withdrawal") : t("deposit"),
      tx.transaction_id || tx.payment_id || tx.id, tx.reference || (tx.type === "WITHDRAWAL" || tx.type === "withdrawal" ? tx.bank_account || "" : "") || "",
      tx.merchant?.name || tx.merchant?.code || "", tx.reseller?.name || "Direct",
      tx.client_bank_name || tx.bank_name || tx.bank_code || "", (tx.amount ?? 0).toString(), tx.status, tx.created_date || tx.createdAt,
    ]);
    const csv = [hdr.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `transactions-${new Date().toISOString().slice(0, 10)}.csv` });
    a.click();
  }, [combinedData, t]);

  /* page pills */
  const pills = useMemo(() => {
    const start = Math.max(0, Math.min(page - 2, totalPages - 5));
    return Array.from({ length: Math.min(totalPages, 5) }, (_, i) => start + i);
  }, [page, totalPages]);

  return (
    <div className="max-w-[1440px] mx-auto md:p-8 flex flex-col gap-6">

      {/* ── Header ── */}
      <div className="tx-hdr">
        <div className="tx-hdr-l">
          <div className="tx-live">
            <span className="tx-dot-wrap"><span className="tx-dot-ring" /><span className="tx-dot" /></span>
            <span className="tx-live-lbl">Live · 5s refresh</span>
          </div>
          <h1 className="tx-title">{t("transactions")}</h1>
          <p className="tx-count"><b>{totalTransactions.toLocaleString()}</b> {t("transactionsFound")}</p>
        </div>
        <div className="tx-chips">
          <div className="tx-chip tx-chip-c">
            <ArrowDownToLine className="tx-chip-ico" />
            <div><p className="tx-chip-lbl">Deposits</p><p className="tx-chip-val">{depCount.toLocaleString()}</p></div>
          </div>
          <div className="tx-chip tx-chip-e">
            <ArrowUpFromLine className="tx-chip-ico" />
            <div><p className="tx-chip-lbl">Withdrawals</p><p className="tx-chip-val">{wdCount.toLocaleString()}</p></div>
          </div>
          <div className="tx-chip tx-chip-p">
            <Layers className="tx-chip-ico" />
            <div><p className="tx-chip-lbl">Total</p><p className="tx-chip-val">{totalTransactions.toLocaleString()}</p></div>
          </div>
        </div>
      </div>

      {/* ── Filters Panel ── */}
      <div className="tx-panel">
        <div className="tx-panel-bar">
          <div className="tx-panel-bar-l">
            <div className="tx-panel-icon tx-panel-icon-c"><Activity /></div>
            <span className="tx-panel-title">{t("filters")}</span>
            <span className="tx-line" />
          </div>
          <button className="tx-export-btn" onClick={handleExport}>
            <ArrowDownToLine />{t("export") || "Export"}
          </button>
        </div>
        <div className="tx-filters-pad tx-filters">
          <TransactionFilters filters={filters} onFilterChange={handleFilter} merchants={merchants} providers={providerOptions} onExport={handleExport} onReset={handleReset} />
        </div>
      </div>

      {/* ── Table Panel ── */}
      <div className="tx-panel">
        <div className="tx-panel-bar">
          <div className="tx-panel-bar-l">
            <div className="tx-panel-icon tx-panel-icon-b"><Activity /></div>
            <span className="tx-panel-title">{t("recentTransactions") || "Transactions"}</span>
          </div>
          {isLoading && (
            <div className="tx-sync">
              <Sparkles className="tx-sync-ico" />
              <span className="tx-sync-lbl">Syncing…</span>
            </div>
          )}
        </div>
        <div className="tx-table-wrap">
          <TransactionTable transactions={paginated} isLoading={isLoading} onView={setSelected} merchants={merchants} resellers={resellers} isQRCode={false} mixedTypes={true} />
        </div>
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="tx-pag">
          <span className="tx-pag-info">
            <b>{(page * PAGE_SIZE + 1).toLocaleString()}–{Math.min((page + 1) * PAGE_SIZE, totalTransactions).toLocaleString()}</b>
            {" "}of{" "}
            <em>{totalTransactions.toLocaleString()}</em>
          </span>
          <div className="tx-pag-ctrl">
            <button className="tx-pag-btn" onClick={() => setPage(page - 1)} disabled={page === 0}><ChevronLeft /></button>
            {pills.map((p) => (
              <button key={p} className={`tx-pag-pill${p === page ? " active" : ""}`} onClick={() => setPage(p)}>{p + 1}</button>
            ))}
            <button className="tx-pag-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}><ChevronRight /></button>
          </div>
        </div>
      )}

      {/* Detail */}
      <TransactionDetail transaction={selected} open={!!selected} onClose={() => setSelected(null)} isQRCode={selected?.type !== "WITHDRAWAL"} />
    </div>
  );
}